import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarSync, Check, Copy, RefreshCw } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  detectPlatform,
  getCalendarFeedUrl,
  getGoogleCalendarAddByUrl,
  getGoogleCalendarSubscribeUrl,
  getWebcalUrl,
  isMobilePlatform,
} from "@/lib/calendarSync";

export function CalendarSyncButton({ accent = "blue", className = "" }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const accentClass = accent === "purple"
    ? "bg-purple-600 hover:bg-purple-700"
    : "bg-blue-600 hover:bg-blue-700";

  const openDialog = async () => {
    setOpen(true);
    if (!token) {
      try {
        const res = await api.get("/calendar/token");
        setToken(res.data.token);
      } catch {
        toast.error("Impossible de charger le lien agenda");
        setOpen(false);
      }
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={openDialog}
        className={`rounded-full border-slate-200 shrink-0 ${className}`}
      >
        <CalendarSync className="w-4 h-4 mr-2" />
        Sync agenda
      </Button>
      <CalendarSyncDialog
        open={open}
        onOpenChange={setOpen}
        token={token}
        onTokenChange={setToken}
        accent={accent}
        accentClass={accentClass}
      />
    </>
  );
}

function CalendarSyncDialog({ open, onOpenChange, token, onTokenChange, accent, accentClass }) {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const feedUrl = token ? getCalendarFeedUrl(token) : "";
  const webcalUrl = feedUrl ? getWebcalUrl(feedUrl) : "";
  const googleAddUrl = feedUrl ? getGoogleCalendarAddByUrl(feedUrl) : "";
  const googleCidUrl = feedUrl ? getGoogleCalendarSubscribeUrl(feedUrl) : "";
  const platform = detectPlatform();
  const mobile = isMobilePlatform();

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copie impossible — sélectionnez le lien manuellement");
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post("/calendar/token/regenerate");
      onTokenChange(res.data.token);
      setCopied(false);
      toast.success("Lien régénéré — réabonnez votre agenda");
    } catch {
      toast.error("Erreur lors de la régénération");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <CalendarSync className={`w-5 h-5 ${accent === "purple" ? "text-purple-600" : "text-blue-600"}`} />
            Synchroniser mon agenda
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 leading-relaxed">
            Abonnez-vous une fois à ce calendrier. Les ajouts, modifications et suppressions de séances
            se synchronisent automatiquement (généralement sous 15 min à 1 h selon votre appareil).
          </p>

          {token ? (
            <>
              {mobile ? (
                <div className="space-y-2">
                  {platform === "ios" && (
                    <p className="text-xs text-slate-500 text-center">
                      Ouvre Apple Calendrier pour s&apos;abonner au flux
                    </p>
                  )}
                  {platform === "android" && (
                    <p className="text-xs text-slate-500 text-center">
                      Ouvre Google Agenda pour ajouter le calendrier par URL
                    </p>
                  )}
                  <div className="flex flex-col gap-2">
                    <a
                      href={webcalUrl}
                      className={`flex items-center justify-center gap-2 w-full text-white rounded-full py-3 text-sm font-semibold transition-colors ${accentClass}`}
                    >
                      <CalendarSync className="w-4 h-4" />
                      Apple Calendrier (iOS)
                    </a>
                    <a
                      href={googleAddUrl}
                      className="flex items-center justify-center gap-2 w-full bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-full py-3 text-sm font-semibold transition-colors"
                    >
                      <CalendarSync className="w-4 h-4" />
                      Google Agenda (Android)
                    </a>
                  </div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex flex-col items-center gap-2 bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">iPhone / iPad</p>
                    <a href={webcalUrl} target="_blank" rel="noreferrer">
                      <QRCodeSVG value={webcalUrl} size={140} bgColor="#f8fafc" fgColor="#1e293b" level="M" />
                    </a>
                    <p className="text-xs text-slate-400 text-center">Scannez → Apple Calendrier</p>
                  </div>
                  <div className="flex flex-col items-center gap-2 bg-slate-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Android</p>
                    <a href={googleAddUrl} target="_blank" rel="noreferrer">
                      <QRCodeSVG value={googleAddUrl} size={140} bgColor="#f8fafc" fgColor="#1e293b" level="M" />
                    </a>
                    <p className="text-xs text-slate-400 text-center">Scannez → Google Agenda</p>
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs text-slate-500 mb-1.5">Lien d&apos;abonnement (HTTPS) :</p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={feedUrl}
                    className="flex-1 text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 font-mono text-slate-600 truncate"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button variant="outline" size="icon" onClick={copyLink} className="shrink-0">
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer font-medium text-slate-600 hover:text-slate-800">
                  Instructions manuelles + forcer la mise à jour
                </summary>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
                    <p className="font-semibold text-slate-700">iPhone / iPad</p>
                    <ol className="space-y-1 list-decimal list-inside text-slate-500">
                      <li>Copiez le lien HTTPS ci-dessus</li>
                      <li>Réglages → <strong>Apps → Calendrier</strong></li>
                      <li><strong>Comptes → Ajouter un compte → Autre</strong></li>
                      <li><strong>Ajouter un cal. avec abonnement</strong></li>
                      <li>Collez le lien → <strong>S&apos;abonner</strong></li>
                      <li>Pour forcer : Calendrier → <strong>Calendriers</strong> → tirez vers le bas</li>
                    </ol>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
                    <p className="font-semibold text-slate-700">Android</p>
                    <ol className="space-y-1 list-decimal list-inside text-slate-500">
                      <li>Copiez le lien HTTPS ci-dessus</li>
                      <li>Ouvrez <strong>calendar.google.com</strong></li>
                      <li>À gauche : <strong>+ → Autres agendas</strong></li>
                      <li><strong>À partir de l&apos;URL</strong></li>
                      <li>Collez le lien → <strong>Ajouter</strong></li>
                      <li>Pour forcer : menu ⋮ → <strong>Actualiser</strong></li>
                    </ol>
                    <a
                      href={googleCidUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-2 text-blue-600 hover:underline"
                    >
                      Lien alternatif Google
                    </a>
                  </div>
                </div>
              </details>
            </>
          ) : (
            <div className="space-y-3">
              <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
              <div className="h-40 bg-slate-100 rounded-xl animate-pulse mx-auto w-40" />
            </div>
          )}

          <div className="flex items-center justify-between pt-1 border-t border-slate-100">
            <p className="text-xs text-slate-400">Révoquer l&apos;accès en régénérant le lien</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={regenerate}
              disabled={regenerating || !token}
              className="text-xs text-slate-500"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${regenerating ? "animate-spin" : ""}`} />
              Régénérer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}